import {
  Badge,
  Button,
  ButtonList,
  Column,
  Columns,
  DropdownButton,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { ComponentType, lazy, PureComponent, Suspense } from 'react';

import { NavigatePrompt, NavigatePromptProps } from 'components/navigatePrompt';
import { RouteComponentProps, withRouter } from 'hocs/router';

import type { CanvasExtraData } from '../flowManager/canvas/types/extraData';
import type { NodeAttributes } from '../flowManager/canvas/types/node';
import type {
  NodeDataT,
  NodeT,
  NodeTypeComponentsListT,
} from '../flowManager/types';

import styles from './flowEditorChrome.module.scss';
import { TitleInput } from './titleInput';
import { FlowType } from './types';

const CanvasEditor = lazy(
  () => import(/* webpackChunkName: "flow-canvas" */ '../flowManager/canvas'),
);

type ButtonCompPropsT<TExtraData = CanvasExtraData> = {
  /** Extra data */
  extraData: TExtraData;
  /** On change callback */
  onChange: (data: NodeDataT) => void;
};

export type FlowPageState = {
  shouldGoBack: boolean;
  backButtonText: string;
  goBackPath?: string;
} | null;

type PropsT<
  TNode extends NodeT,
  TExtraData = CanvasExtraData,
  TBPROPS = any,
> = {
  /** Extra classnames */
  className?: string;
  /** Initial data */
  initialData: NodeDataT<TNode[]>;
  /** The node types to support */
  nodeTypes: NodeTypeComponentsListT | Partial<NodeAttributes>;
  /** On change callback */
  onChange: (data: NodeDataT) => void;
  /** On save callback */
  onSave: () => void;
  /** On activate callback */
  onActivate: (data: NodeDataT) => void;
  /** Does the flow contain unsaved changes? */
  hasUnsavedChanges?: boolean;
  /** Component for additional inputs in topleft of toolbar */
  topleftButtonComponent?: ComponentType<
    ButtonCompPropsT<TExtraData> & TBPROPS
  >;
  /** Props for topleft toolbar */
  topleftButtonComponentProps?: TBPROPS;
  /** Extra-data to be passed to nodes */
  extraData: TExtraData;
  /** Highlight invalid nodes, disabled or array of node IDs */
  invalidNodes: Array<string> | null;
  /** Show custom error message */
  errorMessage: string | null;
  /** Flowtype */
  flowType: FlowType;
  /** Disable flow title edit */
  disableTitleEdit?: boolean;
  /** The path of the back button */
  goBackPath: string;
  /** Props for NavigatePrompt component */
  navigatePromptProps?: Partial<NavigatePromptProps>;
} & RouteComponentProps;

type StateT<TNode extends NodeT> = {
  actions: TNode[];
  active: boolean;
  disableZoomerControls: boolean;
  name: string;
  showFlow: boolean;
};

type DebugStructure = Array<{
  id: string;
  type: string;
  connection: string;
  children: DebugStructure;
}>;

export class FlowEditorChromeImpl<
  TNode extends NodeT,
  TExtraData extends CanvasExtraData = CanvasExtraData,
> extends PureComponent<PropsT<TNode, TExtraData>, StateT<TNode>> {
  constructor(props: PropsT<TNode, TExtraData>) {
    super(props);

    const {
      initialData: { name, actions, active },
    } = props;

    this.state = {
      actions,
      active,
      disableZoomerControls: false,
      name,
      showFlow: true,
    };

    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleActionsChange = this.handleActionsChange.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleActivateClick = this.handleActivateClick.bind(this);
    this.handleOnActivate = this.handleOnActivate.bind(this);
    this.handleLeave = this.handleLeave.bind(this);
    this.handleDebug = this.handleDebug.bind(this);
    this.toggleZoomerControls = this.toggleZoomerControls.bind(this);
    this.handleImport = this.handleImport.bind(this);
    this.handleExport = this.handleExport.bind(this);
  }

  getFlowData(): NodeDataT {
    const { initialData } = this.props;
    const { name, actions, active } = this.state;
    return {
      ...initialData,
      actions,
      name,
      active,
    };
  }

  handleOnChange(): void {
    const { onChange } = this.props;
    onChange(this.getFlowData());
  }

  handleActionsChange(newActions: NodeT[]): void {
    this.setState(
      {
        actions: newActions as TNode[],
      },
      this.handleOnChange,
    );
  }

  handleNameChange(newName: string): void {
    this.setState(
      {
        name: newName,
      },
      this.handleOnChange,
    );
  }

  handleSaveClick(): void {
    const { onSave, flowType } = this.props;
    ampli.flowSaveClicked({
      flowType,
    });
    onSave();
  }

  handleActivateClick(): void {
    const { flowType } = this.props;
    ampli.flowActivateClicked({
      flowType,
    });

    this.setState(
      {
        active: true,
      },
      this.handleOnActivate,
    );
  }

  handleOnActivate(): void {
    const { onActivate } = this.props;
    onActivate(this.getFlowData());
  }

  handleLeave = (): void => {
    const { hasUnsavedChanges, goBackPath, flowType, navigate, location } =
      this.props;
    const { state } = location;

    ampli.flowBackToSettingsClicked({
      warned: !!hasUnsavedChanges,
      flowType,
      path: state?.goBackPath || goBackPath,
    });

    if (state?.shouldGoBack) {
      navigate(-1);
    } else {
      navigate(goBackPath);
    }
  };

  toggleZoomerControls(toggleState: boolean | null): void {
    const { disableZoomerControls } = this.state;
    this.setState({
      disableZoomerControls:
        typeof toggleState === 'boolean' ? toggleState : !disableZoomerControls,
    });
  }

  handleDebug(): void {
    const { actions } = this.state;
    const initNode = actions.find((act) => act.nodeType === 'init');

    if (initNode) {
      const structure = this.getStructure(actions, null);
      console.log(JSON.stringify(structure, null, 2));
    }
  }

  getStructure(actions: Array<NodeT>, parentId: string | null): DebugStructure {
    const children = actions.filter((act) => act.parentId === parentId);
    return children.map((act) => ({
      id: act.id,
      type: act.nodeType,
      connection: act.connectionLabel,
      children: this.getStructure(actions, act.id),
    }));
  }

  handleExport(): void {
    const copyText = JSON.stringify(this.getFlowData());
    const body = document.body as HTMLBodyElement;
    if (copyText && document.body) {
      const el = document.createElement('textarea');
      el.value = copyText;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      body.appendChild(el);
      el.select();
      document.execCommand('copy');
      body.removeChild(el);
    }
    window.alert('Flow copied to clipboard!');
  }

  handleImport(): void {
    const flowData = JSON.parse(window.prompt('Paste flow') || '');

    if (flowData && flowData.actions && flowData.actions.length) {
      this.setState(
        {
          showFlow: false,
          actions: flowData.actions,
          name: flowData.name,
        },
        () => {
          setTimeout(() => {
            this.setState({
              showFlow: true,
            });
          }, 100);
        },
      );
    }
  }

  render() {
    const {
      nodeTypes,
      onChange,
      hasUnsavedChanges,
      topleftButtonComponent: TopleftButtonComponent,
      topleftButtonComponentProps = {},
      extraData,
      invalidNodes,
      errorMessage,
      flowType,
      disableTitleEdit,
      navigatePromptProps,
      location,
    } = this.props;

    const { active, name } = this.state;

    const showFlowError = (invalidNodes && invalidNodes.length) || errorMessage;
    const showFlowUnsaved = !showFlowError && !!hasUnsavedChanges;
    const flowData = this.getFlowData();
    const backButtonText = location.state?.backButtonText || 'Back to settings';

    return (
      <>
        <NavigatePrompt
          when={!!hasUnsavedChanges}
          message="When you leave this page, all unsaved changes to your flow will be lost."
          {...navigatePromptProps}
        />

        <Columns>
          <Column>
            <Column.Row slottable borderBottom className={styles.header}>
              <Column.Slot placement="start">
                <FlexBox alignItems="center" gap="300">
                  <Button
                    icon="arrow-left"
                    variant="secondary"
                    onClick={this.handleLeave}
                  >
                    {backButtonText}
                  </Button>

                  {!!TopleftButtonComponent && (
                    <TopleftButtonComponent
                      {...topleftButtonComponentProps}
                      isFlowActive={active}
                      extraData={extraData}
                      onChange={onChange}
                    />
                  )}
                </FlexBox>
              </Column.Slot>

              <Column.Slot placement="center">
                {disableTitleEdit ? (
                  <Text.Heading>{name}</Text.Heading>
                ) : (
                  <TitleInput
                    flowType={flowType}
                    initialValue={name}
                    onChange={this.handleNameChange}
                  />
                )}
              </Column.Slot>

              <Column.Slot placement="end">
                <FlexBox alignItems="center" gap="300">
                  {showFlowUnsaved && (
                    <Badge variant="warning" size="medium">
                      Unsaved changes
                    </Badge>
                  )}

                  {showFlowError && (
                    <Badge variant="error" size="medium">
                      {errorMessage || 'Flow contains errors'}
                    </Badge>
                  )}

                  {window.DIXA_DEBUG && (
                    <DropdownButton
                      buttonProps={{
                        variant: 'secondary',
                        style: { marginRight: '8px' },
                      }}
                      dropdown={
                        <ButtonList>
                          <ButtonList.Item
                            icon="bolt"
                            onClick={this.handleDebug}
                          >
                            Log action tree
                          </ButtonList.Item>
                          <ButtonList.Divider />
                          <ButtonList.Item
                            icon="bolt"
                            onClick={this.handleImport}
                          >
                            Load flow
                          </ButtonList.Item>
                          <ButtonList.Item
                            icon="bolt"
                            onClick={this.handleExport}
                          >
                            Export flow
                          </ButtonList.Item>
                        </ButtonList>
                      }
                    >
                      DEBUG
                    </DropdownButton>
                  )}

                  {!!hasUnsavedChanges && (
                    <Button onClick={this.handleSaveClick} variant="primary">
                      Save flow
                    </Button>
                  )}
                  {!hasUnsavedChanges && !active && (
                    <Button
                      variant="primary"
                      onClick={this.handleActivateClick}
                    >
                      Activate flow
                    </Button>
                  )}
                  {!hasUnsavedChanges && active && (
                    <Button variant="secondary" disabled>
                      Active
                    </Button>
                  )}
                </FlexBox>
              </Column.Slot>
            </Column.Row>

            <Column.Row size={1} noHorizontalPadding noVerticalPadding>
              <Suspense fallback={null}>
                <CanvasEditor
                  flow={flowData}
                  flowType={flowType}
                  nodeTypes={nodeTypes as NodeAttributes}
                  extraData={extraData}
                  invalidNodes={invalidNodes || []}
                  onChange={this.handleActionsChange}
                />
              </Suspense>
            </Column.Row>
          </Column>
        </Columns>
      </>
    );
  }
}

export const FlowEditorChrome = withRouter(FlowEditorChromeImpl);
